import React from 'react';
import { Icon } from './ui/Icon';
import { ServerAction, World } from '../types';
import { statusInfo } from '../utils/format';

interface Props {
  world: World;
  onRequest: (world: World, action: ServerAction) => void;
  busy?: boolean;
  /** Icon-only buttons for table rows. */
  compact?: boolean;
  /** Show "Start world" for an offline / errored world (owner and staff). */
  canStart?: boolean;
  /** Show stop / kill for a running world (staff only). */
  canManage?: boolean;
}

/**
 * Power controls for a world. An offline world gets a Start button (when `canStart`), a running world
 * gets stop / kill (when `canManage`), and a transitional state shows a disabled status button. There is
 * no restart: restarting the container is a shutdown to the control service, which would back the world up
 * and delete its server.
 */
export const PowerControls: React.FC<Props> = ({ world, onRequest, busy, compact, canStart, canManage }) => {
  const st = statusInfo(world.status);
  if (st.busy) {
    if (compact) return null;
    return (
      <button type="button" className="btn btn--sm" disabled>
        <Icon name="clock" size={13} /> {st.text}
      </button>
    );
  }

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  if (!st.active) {
    if (!canStart) return null;
    if (compact) {
      return (
        <span className="acts" onClick={stop}>
          <button type="button" className="ibtn" title="Start world" disabled={busy} onClick={() => onRequest(world, 'start')}>
            <Icon name="play" size={13} />
          </button>
        </span>
      );
    }
    return (
      <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={() => onRequest(world, 'start')}>
        <Icon name="play" size={12} /> Start world
      </button>
    );
  }

  if (!canManage) return null;
  if (compact) {
    return (
      <span className="acts" onClick={stop}>
        <button type="button" className="ibtn" title="Stop (backup first)" disabled={busy} onClick={() => onRequest(world, 'stop')}>
          <Icon name="stop" size={12} />
        </button>
        <button type="button" className="ibtn" title="Kill (no backup)" style={{ color: 'var(--danger)' }} disabled={busy} onClick={() => onRequest(world, 'kill')}>
          <Icon name="skull" size={14} />
        </button>
      </span>
    );
  }
  return (
    <>
      <button type="button" className="btn btn--sm btn--danger" disabled={busy} onClick={() => onRequest(world, 'stop')}>
        <Icon name="stop" size={12} /> Stop
      </button>
      <button type="button" className="btn btn--sm btn--danger-solid" disabled={busy} onClick={() => onRequest(world, 'kill')}>
        <Icon name="skull" size={13} /> Kill
      </button>
    </>
  );
};

export default PowerControls;
